import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ChevronDown, Search, LifeBuoy, Mail } from "lucide-react";
import { Footer } from "@/components/Footer";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Support FAQ — LendyTrack" },
      {
        name: "description",
        content:
          "Answers to common LendyTrack questions about account access, loan analysis results, premium features, and data privacy.",
      },
      { property: "og:title", content: "LendyTrack Support FAQ" },
      {
        property: "og:description",
        content: "Help with sign-in, loan analysis results, billing, and how LendyTrack handles your data.",
      },
      { property: "og:url", content: "/faq" },
    ],
    links: [{ rel: "canonical", href: "/faq" }],
  }),
  component: FAQPage,
});

type QA = { q: string; a: string };
type Section = { id: string; title: string; items: QA[] };

const sections: Section[] = [
  {
    id: "account",
    title: "Account access",
    items: [
      {
        q: "How do I create an account?",
        a: "Click Sign in from any page, switch to the Sign up tab, and use email + password or continue with Google. After signing up with email, check your inbox for a verification link before signing in — unverified accounts cannot access premium features.",
      },
      {
        q: "I didn't receive the verification email.",
        a: "Check spam and promotions folders first. Verification emails come from a LendyTrack sender address and usually arrive within a minute. If it still isn't there after 10 minutes, try signing in to resend it, or contact support@lendytrack.com.",
      },
      {
        q: "I forgot my password.",
        a: "On the Sign in screen, choose 'Forgot password?'. We'll email you a secure reset link that expires in one hour. The link takes you to /reset-password where you can set a new one.",
      },
      {
        q: "Why was I locked out after a few wrong passwords?",
        a: "To protect your account, LendyTrack temporarily locks sign-in after 5 failed attempts for 15 minutes. Wait it out or reset your password — the lockout clears automatically.",
      },
      {
        q: "How do I sign out of all devices?",
        a: "Sign out from the dashboard menu. If you suspect unauthorized access, change your password — that invalidates existing sessions across all devices.",
      },
      {
        q: "Can I delete my account?",
        a: "Yes. Email support@lendytrack.com from your registered address and we'll permanently delete your account, saved analyses, and personal data within 30 days, in line with our Privacy Policy.",
      },
    ],
  },
  {
    id: "analysis",
    title: "Loan analysis results",
    items: [
      {
        q: "How does the Loan Analyzer rate an offer?",
        a: "We compute monthly payment, total interest, and cost per dollar borrowed from the APR, fees, principal, and term you enter. We then flag the loan as 'Avoid', 'Compare carefully', or 'Reasonable terms' based on benchmarks like the 36% APR safe-borrowing threshold and the ratio of fees and interest to principal.",
      },
      {
        q: "What does an APR above 36% mean?",
        a: "36% is widely treated as the upper bound of mainstream, non-predatory consumer lending. Anything above it materially raises the cost and risk of the loan. LendyTrack will surface safer alternatives at the same loan amount when available.",
      },
      {
        q: "Why do safer alternatives sometimes not appear?",
        a: "Alternatives are filtered to lenders that actually serve your requested loan amount and have a 'safe' recommendation in our directory. If your amount falls outside common ranges (very small or very large), fewer or no matches will show.",
      },
      {
        q: "Are LendyTrack analyses financial advice?",
        a: "No. LendyTrack provides educational analysis and risk signals. It does not constitute financial, legal, or tax advice, and is not a substitute for guidance from a licensed professional. Always review the actual loan agreement before signing.",
      },
      {
        q: "Can I save my analyses?",
        a: "Premium members can save unlimited analyses to their dashboard, revisit them later, and compare scenarios side-by-side. Free users get the live analyzer without persistent history.",
      },
      {
        q: "Where does lender data come from?",
        a: "Lender profiles aggregate publicly available regulatory filings, lender disclosures, and consumer-reported data. We refresh continuously and welcome corrections via the contact form.",
      },
    ],
  },
  {
    id: "billing",
    title: "Billing & Premium",
    items: [
      {
        q: "What's included in Premium?",
        a: "Unlimited AI Coach chats, the full Loan Analyzer with saved history, every lender profile, and your personal dashboard with saved analyses. See /pricing for current plans.",
      },
      {
        q: "How do I cancel?",
        a: "Open your dashboard and choose 'Manage subscription'. You'll be sent to a secure customer portal where you can cancel; access continues through the end of the paid period.",
      },
      {
        q: "Do you offer refunds?",
        a: "If something went wrong with a recent charge, email support@lendytrack.com within 14 days of the payment and we'll review it case by case.",
      },
    ],
  },
  {
    id: "privacy",
    title: "Data & privacy",
    items: [
      {
        q: "What data does LendyTrack collect?",
        a: "Your email, display name, sign-in metadata, the loan inputs and analyses you save, AI Coach conversations, and basic usage analytics. We do not collect bank credentials, SSNs, or full account numbers. See our Privacy Policy at /privacy for the full list.",
      },
      {
        q: "Do you sell my data?",
        a: "No. LendyTrack does not sell personal information, and we do not share loan analyses or Coach chats with third parties for marketing.",
      },
      {
        q: "Is my data encrypted?",
        a: "All traffic is served over HTTPS (HSTS enforced), and your data is encrypted at rest by our cloud database provider. Authentication tokens are stored using browser-managed secure storage.",
      },
      {
        q: "Who can see my saved analyses and Coach chats?",
        a: "Only you. Row-level security in our database restricts every saved record to the account that created it; even LendyTrack staff cannot read your saved analyses or chat history without an explicit support request from you.",
      },
      {
        q: "How do I request my data or deletion (GDPR / CCPA)?",
        a: "Email privacy requests to support@lendytrack.com from your registered address. We respond within 30 days with an export or confirmation of deletion.",
      },
      {
        q: "Do you use cookies or trackers?",
        a: "We use first-party cookies for authentication and a minimal set of privacy-respecting analytics. We do not use cross-site advertising trackers.",
      },
    ],
  },
];

function FAQPage() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections
      .map((s) => ({
        ...s,
        items: s.items.filter(
          (i) => i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q),
        ),
      }))
      .filter((s) => s.items.length > 0);
  }, [query]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="font-display text-lg font-semibold">LendyTrack</Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/status" className="text-muted-foreground hover:text-foreground">Status</Link>
            <Link to="/contact" className="text-muted-foreground hover:text-foreground">Contact</Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-16">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Support</div>
        <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight">How can we help?</h1>
        <p className="mt-3 text-muted-foreground">
          Answers to the questions we hear most. Can't find what you need?{" "}
          <Link to="/contact" className="font-semibold text-primary hover:underline">Send us a message</Link>.
        </p>

        <div className="mt-8 relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the FAQ…"
            className="w-full rounded-lg border border-border bg-card py-3 pl-10 pr-3 text-sm shadow-soft focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {!query && (
          <nav aria-label="FAQ sections" className="mt-6 flex flex-wrap gap-2">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
              >
                {s.title}
              </a>
            ))}
          </nav>
        )}

        <div className="mt-10 space-y-12">
          {filtered.length === 0 && (
            <p className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
              No matches. Try different keywords, or{" "}
              <Link to="/contact" className="font-semibold text-primary hover:underline">contact support</Link>.
            </p>
          )}
          {filtered.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24">
              <h2 className="font-display text-2xl font-semibold tracking-tight">{s.title}</h2>
              <div className="mt-4 divide-y divide-border rounded-2xl border border-border bg-card shadow-soft">
                {s.items.map((item, i) => (
                  <FAQItem key={i} q={item.q} a={item.a} />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 grid gap-4 rounded-2xl border border-border bg-gradient-to-br from-card to-muted p-6 shadow-soft md:grid-cols-2">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
              <LifeBuoy className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">Still stuck?</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Our team replies within one business day.
              </p>
              <Link
                to="/contact"
                className="mt-3 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
              >
                Open a support request
              </Link>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-accent/10 text-accent">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">Prefer email?</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Write to{" "}
                <a href="mailto:support@lendytrack.com" className="font-semibold text-foreground hover:underline">
                  support@lendytrack.com
                </a>
                .
              </p>
              <Link to="/status" className="mt-3 inline-block text-sm font-semibold text-primary hover:underline">
                Check system status →
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="font-medium">{q}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 text-sm text-muted-foreground">{a}</div>}
    </div>
  );
}
